import {Eye} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {Switch} from '@/components/ui/switch'
import {Label} from '@/components/ui/label'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'

interface DesignerToolbarProps {
    useCaseKey: string
    displayName: string
    active: boolean
    onActiveChange: (active: boolean) => void
    previewActive: boolean
    onPreviewToggle: () => void
}

export function DesignerToolbar({
    useCaseKey,
    displayName,
    active,
    onActiveChange,
    previewActive,
    onPreviewToggle,
}: DesignerToolbarProps) {
    const i18n = useI18n()

    return (
        <TooltipProvider>
            <div className="flex items-center gap-4 border-b bg-background px-4 py-3">
                {/* Title */}
                <div className="flex items-center gap-2">
                    <h1 className="text-lg font-semibold" data-testid="designer-toolbar-title">{displayName}</h1>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <code className="cursor-default rounded bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground">
                                {useCaseKey}
                            </code>
                        </TooltipTrigger>
                        <TooltipContent>{i18n.formLayouts.keyBadgeTooltip}</TooltipContent>
                    </Tooltip>
                </div>

                <div className="ml-auto flex items-center gap-3">
                    {/* Active toggle */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="flex items-center gap-2">
                                <Switch
                                    id="active-toggle"
                                    checked={active}
                                    onCheckedChange={onActiveChange}
                                />
                                <Label htmlFor="active-toggle" className="cursor-pointer text-sm">
                                    {i18n.common.active}
                                </Label>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>{i18n.formLayouts.activeTooltip}</TooltipContent>
                    </Tooltip>

                    {/* Preview */}
                    {!previewActive && (
                        <Button data-testid="designer-preview-btn" variant="outline" size="sm" onClick={onPreviewToggle}>
                            <Eye className="mr-1.5 h-4 w-4" />
                            {i18n.formLayouts.preview}
                        </Button>
                    )}
                </div>
            </div>
        </TooltipProvider>
    )
}
